import { replaceTemplate } from '@china-difi/lib/template';
import { reactive, onMount, onCleanup } from 'cdui-js';
import { uploadFile, uploadCommonFile } from '../../../../api/common';
import { DiFiLoading } from '../../../../components/DiFi/DiFiLoading';
import { prepareImageFileForUpload } from '../../../../lib/compressImageForUpload';
import i18n from '../../../../i18n';
import { showToast } from '../Toast';

const DEFAULT_ACCEPT = 'image/*';
const IMAGE_ACCEPT = 'image/jpeg,image/jpg,image/png,.jpg,.jpeg,.png';
const ALLOWED_MIME = new Set(['image/jpeg', 'image/jpg', 'image/png']);
const ALLOWED_EXT = /\.(jpe?g|png)$/i;

/** 证明类（结单等）：pdf + 截屏常见静态图；禁 gif/heic */
export const PROOF_ACCEPT =
  'image/jpeg,image/png,image/webp,application/pdf,.jpg,.jpeg,.png,.webp,.pdf';
const ALLOWED_PROOF_MIME = new Set([
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'application/pdf',
]);
const ALLOWED_PROOF_EXT = /\.(jpe?g|png|webp|pdf)$/i;
const PROOF_IMAGE_EXT = /\.(jpe?g|png|webp)$/i;

/** 图片默认上限（字节）；未传 maxSize 时使用 */
export const DEFAULT_IMAGE_MAX_BYTES = 10 * 1024 * 1024;
/** 资料/证明类上传（结单、入金凭证、地址证明等）上限 */
export const PROOF_IMAGE_MAX_BYTES = 10 * 1024 * 1024;
/** 开户流程上传（证件 / 银联卡 / 住址证明 / 入金证明等）上限 */
export const OPEN_ACCOUNT_IMAGE_MAX_BYTES = 10 * 1024 * 1024;
/** 公共明文上传用途（与后端 isClientPublicPlainUseFor 对齐） */
const PUBLIC_PLAIN_USE_FOR = new Set(['avatar', 'feedback']);

interface Props {
  key?: string;
  useFor?: string;
  busId?: string;
  isOCR?: boolean;
  class?: any;
  style?: any;
  change?: (e) => void;
  /** 限制文件大小（字节）。未传时图片默认 10MB；证明类 / 开户可显式传 PROOF_ / OPEN_ACCOUNT_IMAGE_MAX_BYTES（同为 10MB） */
  maxSize?: number;
  /** 限制为 jpg/jpeg/png；传入 true 或具体 accept 字符串 */
  restrictImageType?: boolean | string;
  /** 证明类：jpg/jpeg/png/webp/pdf（禁 gif/heic） */
  restrictProofType?: boolean;
  /** 上传中是否在组件内显示转圈，默认 true */
  showLoading?: boolean;
  /**
   * 为 true 时：长边 > 1920 或体积 > 1MB 才压缩后再传；默认 false（开户等保持原样）。
   */
  compressBeforeUpload?: boolean;
}

const isAllowedImageType = (file: File) => {
  if (ALLOWED_MIME.has(file.type)) return true;
  // 部分机型 type 为空，用扩展名兜底
  if (!file.type) return ALLOWED_EXT.test(file.name);
  return false;
};

const isAllowedProofType = (file: File) => {
  if (ALLOWED_PROOF_MIME.has(file.type)) return true;
  if (!file.type) return ALLOWED_PROOF_EXT.test(file.name);
  return false;
};

/** 从 accept 字符串解析 MIME / 扩展名集合，用于自定义 restrictImageType 字符串校验 */
const parseAcceptAllowlist = (accept: string) => {
  const mimes = new Set<string>();
  const exts: string[] = [];
  for (const raw of accept.split(',')) {
    const token = raw.trim().toLowerCase();
    if (!token || token === '*' || token === 'image/*') continue;
    if (token.startsWith('.')) {
      exts.push(token.slice(1).replace(/^\./, ''));
    } else if (token.includes('/')) {
      mimes.add(token);
      if (token === 'image/jpeg') mimes.add('image/jpg');
    }
  }
  const extRe = exts.length ? new RegExp(`\\.(${exts.map((e) => e.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})$`, 'i') : null;
  return { mimes, extRe };
};

const isAllowedByAcceptString = (file: File, accept: string) => {
  const { mimes, extRe } = parseAcceptAllowlist(accept);
  if (file.type && mimes.has(file.type.toLowerCase())) return true;
  if (extRe && extRe.test(file.name)) return true;
  if (!file.type && extRe) return extRe.test(file.name);
  return false;
};

const isPdfFile = (file: File) => {
  if (file.type === 'application/pdf') return true;
  return /\.pdf$/i.test(file.name);
};

/** 按图片做体积限制：image/*，或 type 为空但扩展名是可压缩图片 */
const isImageForSizeCheck = (file: File) => {
  if (file.type.startsWith('image/')) return true;
  if (!file.type) return PROOF_IMAGE_EXT.test(file.name) || ALLOWED_EXT.test(file.name);
  return false;
};

/**
 * 本次选择的体积上限。图片默认 10MB；调用方 maxSize 优先；PDF 等非图也走 maxSize。
 */
const resolveMaxBytes = (file: File, maxSize?: number) => {
  if (isImageForSizeCheck(file) || isPdfFile(file)) {
    if (maxSize != null) return maxSize;
    if (isImageForSizeCheck(file)) return DEFAULT_IMAGE_MAX_BYTES;
  }
  return maxSize;
};

const maxBytesToToastLabel = (maxBytes: number) => {
  const mb = maxBytes / (1024 * 1024);
  return Number.isInteger(mb) ? String(mb) : mb.toFixed(1);
};

const toastImageSizeExceeded = (maxBytes: number) => {
  showToast(
    replaceTemplate(i18n.OpenProgress.Upload.ToastImageSize, maxBytesToToastLabel(maxBytes)),
  );
};

/**
 * 是否走公共明文上传。
 * @param useFor 用途
 * @returns true 公共
 */
function isPublicPlainUseFor(useFor?: string): boolean {
  if (!useFor) return false;
  return PUBLIC_PLAIN_USE_FOR.has(String(useFor).trim().toLowerCase());
}

/** iOS / iPadOS。仅这些设备在关掉选文件面板后禁用上传区 */
const isIOS = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/iphone|ipad|ipod/i.test(ua)) return true;
  return navigator.platform === 'MacIntel' && (navigator.maxTouchPoints ?? 0) > 1;
};

const IOS_PICKER_COOL_MS = 500;

export const UploadImage = (props: Props) => {
  const state = reactive({
    status: '',
    /** iOS：从点击打开面板到禁用结束，整张组件保持半透明 */
    dimmed: false,
    /** iOS：面板关掉后才禁用，避免点开的那一下被 disabled 吃掉 */
    locked: false,
  });

  let fileInput: HTMLInputElement | undefined;
  /** 本次已打开系统选文件面板 */
  let pickerOpened = false;
  /** 本次已选到文件，关掉面板时不要进入禁用 */
  let acceptedFile = false;
  let coolTimer: ReturnType<typeof setTimeout> | undefined;

  /** 取消选文件后丢掉焦点。iOS 否则把下一次点击用来失焦，要点两次才再打开。 */
  const releaseFileInput = () => {
    if (!fileInput) return;
    fileInput.value = '';
    fileInput.blur();
  };

  const clearIosHold = () => {
    state.dimmed = false;
    state.locked = false;
    if (coolTimer) {
      clearTimeout(coolTimer);
      coolTimer = undefined;
    }
  };

  /** 第一次点击就变淡，面板打开期间保持，避免关掉时才套上透明度造成闪烁 */
  const beginIosDim = () => {
    if (!isIOS()) return;
    state.dimmed = true;
  };

  /** 取消选文件后禁用 0.5 秒，半透明不中断 */
  const lockIosAfterClose = () => {
    if (!isIOS()) {
      state.dimmed = false;
      return;
    }
    state.dimmed = true;
    state.locked = true;
    if (coolTimer) clearTimeout(coolTimer);
    coolTimer = setTimeout(() => {
      clearIosHold();
    }, IOS_PICKER_COOL_MS);
  };

  /**
   * 面板关闭。选中文件立刻恢复；取消则接着禁用 0.5 秒。
   */
  const onPickerClosed = () => {
    if (!pickerOpened) return;
    const hadFile = acceptedFile;
    pickerOpened = false;
    acceptedFile = false;
    if (hadFile) {
      clearIosHold();
      return;
    }
    releaseFileInput();
    lockIosAfterClose();
  };

  onMount(() => {
    const onWindowFocus = () => {
      if (document.activeElement === fileInput) fileInput.blur();
      // change 可能紧挨着 focus，延后一拍再判断有没有选到文件
      setTimeout(() => onPickerClosed(), 0);
    };
    window.addEventListener('focus', onWindowFocus);
    onCleanup(() => {
      window.removeEventListener('focus', onWindowFocus);
      if (coolTimer) clearTimeout(coolTimer);
    });
  });

  const acceptAttr = () => {
    if (props.restrictProofType === true) return PROOF_ACCEPT;
    if (props.restrictImageType === true) return IMAGE_ACCEPT;
    if (typeof props.restrictImageType === 'string') return props.restrictImageType;
    return DEFAULT_ACCEPT;
  };

  const showLoading = () => props.showLoading !== false && state.status === 'loading';

  const handleChange = async (e) => {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) acceptedFile = true;
    if (!file) return;

    if (props.restrictProofType === true) {
      if (!isAllowedProofType(file)) {
        showToast(i18n.OpenProgress.Upload.ToastProofType);
        input.value = '';
        props?.change?.({ status: 'error' });
        return;
      }
    } else if (props.restrictImageType === true) {
      if (!isAllowedImageType(file)) {
        showToast(i18n.OpenProgress.Upload.ToastImageType);
        input.value = '';
        props?.change?.({ status: 'error' });
        return;
      }
    } else if (typeof props.restrictImageType === 'string') {
      if (!isAllowedByAcceptString(file, props.restrictImageType)) {
        showToast(i18n.OpenProgress.Upload.ToastImageType);
        input.value = '';
        props?.change?.({ status: 'error' });
        return;
      }
    }

    const maxBytes = resolveMaxBytes(file, props.maxSize);
    const useCompress =
      props.compressBeforeUpload === true && isImageForSizeCheck(file) && !isPdfFile(file);

    if (!useCompress && maxBytes != null && file.size > maxBytes) {
      toastImageSizeExceeded(maxBytes);
      input.value = '';
      props?.change?.({ status: 'error' });
      return;
    }

    try {
      state.status = 'loading';
      props?.change({
        status: 'loading',
      });

      let fileToUpload = file;
      if (useCompress && maxBytes != null) {
        fileToUpload = await prepareImageFileForUpload(file, { maxUploadBytes: maxBytes });
      }

      if (maxBytes != null && fileToUpload.size > maxBytes) {
        toastImageSizeExceeded(maxBytes);
        input.value = '';
        props?.change?.({ status: 'error' });
        return;
      }

      let data: any;
      if (isPublicPlainUseFor(props.useFor)) {
        data = await uploadCommonFile(fileToUpload, props.useFor, props.busId);
      } else {
        data = await uploadFile({
          file: fileToUpload,
          useFor: props.useFor,
          busId: props.busId,
          isOCR: props.isOCR,
        });
      }
      // 本会话本地预览；调用方负责 revoke，或交给 AuthSensitiveImage(path=blob) 由父组件生命周期管理
      const localPreviewUrl = URL.createObjectURL(fileToUpload);
      props?.change({
        status: 'success',
        localPreviewUrl,
        fileKind: isPdfFile(fileToUpload) ? 'pdf' : 'image',
        ...data,
      });
    } catch (err) {
      props.change({
        status: 'error',
      });
      // 业务错误已由 http-interceptor toast（reject 形如 "100030 OCR..."）；无文案时再兜底
      const raw =
        typeof err === 'string' ? err : (err as { message?: string })?.message || '';
      if (!raw) {
        showToast('上传失败');
      }
    } finally {
      state.status = '';
      input.value = '';
    }
  };

  return (
    <div
      class={`upload-image relative ${props.class || ''} ${state.dimmed ? 'upload-image-cooling' : ''}`.trim()}
      style={{
        ...(props.style && typeof props.style === 'object' ? props.style : null),
        overflow: 'hidden',
        'border-radius': 'inherit',
      }}
    >
      {showLoading() ? (
        <div
          class="upload-image-loading flex align-center justify-center bg-c-XD"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            height: '100%',
            'z-index': 3,
          }}
        >
          <DiFiLoading overlay={false} size="sm" />
        </div>
      ) : (
        <input
          ref={(el: HTMLInputElement | null) => {
            fileInput = el ?? undefined;
          }}
          type="file"
          accept={acceptAttr()}
          disabled={state.locked}
          ontouchstart={() => {}}
          onclick={() => {
            pickerOpened = true;
            acceptedFile = false;
            beginIosDim();
          }}
          oncancel={() => onPickerClosed()}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            margin: 0,
            padding: 0,
            opacity: 0,
            cursor: 'pointer',
          }}
          onchange={(e) => handleChange(e)}
        />
      )}
    </div>
  );
};
